// What the two brain chats do with a file somebody drops on them.
//
// Both TaskCoach ("Walk me through this") and BrainChat ("Ask the brain") take
// attachments, and before this file they each did it differently and badly:
// TaskCoach read screenshots and nothing else, BrainChat read screenshots and
// small PDFs. Mercedes, 21 Aug: "I need it to be able to read any file type I
// attach, including PDFs", and "files of any size, not just small ones".
//
// THE SIZE CEILING IS NOT OURS TO SET, AND THAT IS THE WHOLE DESIGN HERE.
// Every attachment used to travel as base64 inside the request body, and
// Netlify cuts a function call off around 6MB, so a 9MB bank statement was
// refused before it ever reached Claude. A file bigger than the inline budget
// now goes browser -> R2 on a presigned PUT (no ceiling at all) and Claude is
// handed a short-lived signed URL to fetch it from. The real remaining limit is
// Anthropic's: 32MB and 100 pages per PDF. We stop a bit under that and say so
// in plain words rather than failing with a number nobody can act on.
//
// Signed URLs expire in an hour, so a document that lives in R2 keeps its KEY on
// the message and the URL is minted fresh on every API call. A conversation left
// open over lunch keeps working.

import { fileToResizedBase64 } from './mediaUpload';
import { getAccessToken } from '../services/auth';

// Anthropic's own PDF cap is 32MB / 100 pages. Leave headroom for the rest of
// the conversation rather than sitting on the line.
export const MAX_ATTACH_BYTES = 30 * 1024 * 1024;
// Under this, skip the R2 round trip and send the bytes inline. Netlify's body
// cap is ~6MB and base64 inflates by a third, so 3.2MB of file is ~4.3MB on the
// wire and still leaves room for the transcript.
export const INLINE_MAX_BYTES = 3200000;
// A spreadsheet turned into text can be enormous. This is roughly 30k tokens,
// which is a lot of ledger and still leaves the model room to answer.
export const TEXT_MAX_CHARS = 120000;

export const IMG_MAX_EDGE = 1400;
export const IMG_QUALITY = 0.82;

const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i;
const PDF_RE = /\.pdf$/i;
const SHEET_RE = /\.(xlsx|xlsm|xls)$/i;
// Bank and accounting downloads (.ofx, .qfx, .qbo) are plain text underneath,
// so Claude can read them directly once we hand it the contents.
const TEXT_RE = /\.(csv|tsv|txt|md|json|xml|log|ofx|qfx|qbo|htm|html)$/i;

export function classifyFile(f) {
  const name = f?.name || '';
  const type = f?.type || '';
  if (/^image\//.test(type) || IMAGE_RE.test(name)) return 'image';
  if (/pdf$/i.test(type) || PDF_RE.test(name)) return 'pdf';
  if (SHEET_RE.test(name) || /spreadsheetml|ms-excel/.test(type)) return 'sheet';
  if (TEXT_RE.test(name) || /^text\//.test(type) || type === 'application/json') return 'text';
  return 'other';
}

// Dragging an attachment straight out of an Outlook email.
//
// Mercedes, 25 Aug: "we can drag files that are saved to our computer, but dragging
// a file directly out of an email doesn't work ... every file has to be saved to the
// computer first, which is an extra step on every invoice."
//
// The reason is that Outlook does not hand over a file on disk. There is no file on
// disk: the attachment lives inside the message, so Windows offers it as a VIRTUAL
// file (the CFSTR_FILEDESCRIPTOR / CFSTR_FILECONTENTS shell formats). For those,
// `dataTransfer.files` is EMPTY and `item.getAsFile()` returns NULL, which is exactly
// what the old code checked, so an Outlook drag looked identical to dropping nothing
// at all. Chrome can still produce the bytes, through the entry APIs.
//
// TWO RULES, and both are easy to get wrong:
//  1. `webkitGetAsEntry()` / `getAsFileSystemHandle()` must be CALLED SYNCHRONOUSLY,
//     inside the drop handler. A DataTransfer is neutered the moment the handler
//     returns, so anything awaited before the call gets an empty list. That is why
//     this function does one synchronous pass to collect handles and entries, and
//     only then awaits them.
//  2. `getAsFile()` has to be tried FIRST. For an ordinary file on disk it is the
//     cheap answer, and the entry path is the fallback rather than the other way
//     round.
export async function filesFromDrop(dt) {
  if (!dt) return [];
  const direct = [];
  const pending = [];

  // ── synchronous pass. Nothing may be awaited above this line. ──
  const items = dt.items ? Array.from(dt.items) : [];
  for (const it of items) {
    if (it.kind !== 'file') continue;
    let f = null;
    try { f = it.getAsFile(); } catch (_) { f = null; }
    if (f) { direct.push(f); continue; }
    // A virtual file. Ask BOTH ways, and ask now: getAsFileSystemHandle is the newer
    // API and webkitGetAsEntry has been there since Chrome 76, and which one answers
    // depends on the browser and on what Outlook offered. Both have to be requested
    // in this synchronous pass, so whichever one fails we still hold the other -
    // asking for the second only after the first rejects is too late, the
    // DataTransfer is gone by then.
    let handleP = null;
    let entry = null;
    try {
      if (typeof it.getAsFileSystemHandle === 'function') handleP = it.getAsFileSystemHandle();
    } catch (_) { handleP = null; }
    try {
      const e = it.webkitGetAsEntry && it.webkitGetAsEntry();
      if (e && e.isFile) entry = e;
    } catch (_) { entry = null; }
    if (!handleP && !entry) continue;
    const fromEntry = () => (entry
      ? new Promise((res) => { entry.file(res, () => res(null)); })
      : Promise.resolve(null));
    pending.push(
      (handleP
        ? handleP.then(h => (h && h.getFile ? h.getFile() : null)).catch(() => null)
        : Promise.resolve(null)
      ).then(file => file || fromEntry())
    );
  }
  const fallback = (!direct.length && dt.files && dt.files.length) ? Array.from(dt.files) : [];
  // ── end of the synchronous pass ──

  const resolved = await Promise.all(pending.map(p => p.catch(() => null)));
  return [...direct, ...fallback, ...resolved].filter(Boolean);
}

export const dragCarriesFiles = (dt) => Array.from(dt?.types || []).includes('Files');

export const prettySize = (n) => (n >= 1024 * 1024
  ? `${(n / 1024 / 1024).toFixed(1)}MB`
  : `${Math.max(1, Math.round(n / 1024))}KB`);

const readAsText = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(new Error(`Could not read ${file.name}.`));
  r.onload = () => resolve(String(r.result || ''));
  r.readAsText(file);
});

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(new Error(`Could not read ${file.name}.`));
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.readAsDataURL(file);
});

// xlsx is a big library and most attachments are not spreadsheets, so it is
// pulled in only when one actually turns up. CRA splits it into its own chunk.
async function sheetToText(file) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const parts = [];
  for (const name of wb.SheetNames) {
    const csv = XLSX.utils.sheet_to_csv(wb.Sheets[name]);
    if (!csv.trim()) continue;
    parts.push(`--- sheet: ${name} ---\n${csv}`);
  }
  return parts.join('\n\n') || '(the workbook has no readable cells)';
}

const capText = (s, name) => (s.length <= TEXT_MAX_CHARS ? s : [
  s.slice(0, TEXT_MAX_CHARS),
  '',
  `[${name} is longer than this. The first ${TEXT_MAX_CHARS.toLocaleString()} characters are shown, so ask for a smaller export if the rest matters.]`,
].join('\n'));

async function presign(action, body) {
  const r = await fetch('/.netlify/functions/r2-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
    body: JSON.stringify({ action, ...body }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `The file store said no (${r.status}).`);
  return d;
}

// Straight from the browser to R2. Nothing about this path is size limited, which
// is the entire point of it.
export async function uploadToR2(file, prefix = 'accounting') {
  const p = await presign('presign-doc', {
    filename: file.name || `attachment-${Date.now()}`,
    contentType: file.type || 'application/octet-stream',
    prefix,
  });
  if (!p.uploadUrl) throw new Error('Could not prepare the upload.');
  const put = await fetch(p.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': p.contentType || file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status}). If this keeps happening, tell Greg.`);
  return { key: p.key, contentType: p.contentType };
}

export const signRead = async (key) => (await presign('presign-read', { key })).url;

/**
 * Turn a dropped file into something a chat message can carry.
 *
 * `store` forces the original into R2 even when it is small enough to inline.
 * TaskCoach wants that, because a document that answers a question is evidence
 * and belongs on the task, not only in a chat window. BrainChat has no task to
 * hang it on, so it only stores what it has to.
 *
 * Returns a descriptor. An ordinary unreadable file is not an error: it comes
 * back with readable false and a note the caller can show.
 */
export async function prepareAttachment(file, { prefix = 'accounting', store = false } = {}) {
  const kind = classifyFile(file);
  const name = file.name || `attachment-${Date.now()}`;

  if (file.size > MAX_ATTACH_BYTES) {
    throw new Error(`${name} is ${prettySize(file.size)}. Anything over ${prettySize(MAX_ATTACH_BYTES)} is past what Claude will read in one go, so send the pages or the months that matter.`);
  }

  if (kind === 'image') {
    // Resized on the way in: Anthropic wants the long edge under ~1568px, and a
    // 40MP phone photo would fill the request body on its own.
    const r = await fileToResizedBase64(file, IMG_MAX_EDGE, IMG_QUALITY);
    const att = {
      kind: 'image', name, readable: true, file,
      base64: r.base64, mediaType: r.mediaType || 'image/jpeg',
    };
    if (store) {
      try { att.r2Key = (await uploadToR2(file, prefix)).key; att.stored = true; } catch (_) { /* reading it still works */ }
    }
    return att;
  }

  if (kind === 'pdf') {
    if (store || file.size > INLINE_MAX_BYTES) {
      const { key } = await uploadToR2(file, prefix);
      return { kind: 'pdf', name, r2Key: key, stored: true, readable: true, bytes: file.size };
    }
    return { kind: 'pdf', name, base64: await readAsBase64(file), readable: true, bytes: file.size };
  }

  if (kind === 'sheet' || kind === 'text') {
    const raw = kind === 'sheet' ? await sheetToText(file) : await readAsText(file);
    const att = { kind: 'text', name, text: capText(raw, name), readable: true, bytes: file.size };
    if (store) {
      try { att.r2Key = (await uploadToR2(file, prefix)).key; att.stored = true; } catch (_) { /* reading it still works */ }
    }
    return att;
  }

  // Everything else: keep the file, and be honest that the words inside it are
  // not reachable. "Save it as a PDF" is a fix she can act on in ten seconds.
  // "Unsupported file type" is not.
  const ext = (name.split('.').pop() || 'file').toLowerCase();
  let r2Key = null;
  try { ({ key: r2Key } = await uploadToR2(file, prefix)); } catch (_) { /* fall through to the note */ }
  return {
    kind: 'file',
    name,
    r2Key,
    stored: !!r2Key,
    readable: false,
    note: r2Key
      ? `${name} is saved, but I cannot read inside a .${ext}. Save it as a PDF, or export it to CSV, and I will read it.`
      : `I cannot read a .${ext}, and that one did not save either. Save it as a PDF and try again.`,
  };
}

/**
 * The Anthropic content blocks for one attachment.
 *
 * Note there is no `title` on an image block. Anthropic rejects the whole
 * request with a 400 if you put one there (verified against the live API on
 * 21 Aug 2026, and that is what was breaking every screenshot sent from Ask the
 * Brain). `title` IS valid on a document block, so PDFs keep theirs.
 */
export async function blocksFor(att) {
  if (!att) return [];
  if (att.kind === 'image' && att.base64) {
    return [{ type: 'image', source: { type: 'base64', media_type: att.mediaType || 'image/jpeg', data: att.base64 } }];
  }
  if (att.kind === 'pdf') {
    if (att.base64) {
      return [{ type: 'document', title: att.name, source: { type: 'base64', media_type: 'application/pdf', data: att.base64 } }];
    }
    if (att.r2Key) {
      // Minted per call, so an hour-old conversation about the same statement
      // does not start failing on an expired link.
      return [{ type: 'document', title: att.name, source: { type: 'url', url: await signRead(att.r2Key) } }];
    }
  }
  if (att.kind === 'text' && att.text) {
    return [{ type: 'document', title: att.name, source: { type: 'text', media_type: 'text/plain', data: att.text } }];
  }
  return [];
}

// What to keep in the stored transcript. The bytes and the extracted text are
// dropped (base64 in a JSONB column we read on every open is a slow leak), but
// the R2 key stays, so a document reopened tomorrow is still readable.
export const forStorage = (att) => ({
  kind: att.kind,
  name: att.name,
  r2Key: att.r2Key || null,
  readable: !!att.readable,
});

// claude-proxy deliberately does not pass Anthropic's error text through to the
// browser, so a 400 arrives as a bare number. When there was an attachment on
// the message, say the thing she can actually act on.
export const explainSendFailure = (msg, hadAttachment) => (
  hadAttachment && /\b400\b/.test(String(msg || ''))
    ? 'That did not go through. If it was a PDF, it is most likely over 100 pages or too big to read in one piece. Send the pages that matter and it will go straight through.'
    : msg
);
